import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Input, Modal } from '@cognitiv/cyprus-ui';
import { BottomNavigation } from 'components/bottom-navigation/BottomNavigation';
import { ModalHeader } from 'components/modal-header/ModalHeader';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectRunScheduleBatch } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { selectScheduledBatch } from 'ducks/scheduled-batch/selectors';
import { selectScheduledBatchParameters } from 'ducks/scheduled-batch-parameters/selectors';
import { runScheduledBatch } from 'ducks/scheduled-batches/operators';
import { updateSettings, updateSuccess } from 'ducks/settings/slices';
import { ERROR_LEVEL } from 'ducks/types/common-types';
import { ScheduleBatchPayload } from 'ducks/types/payload-types';
import { ProjectParameters } from 'ducks/types/store-types';
import { useValidate } from 'hooks/useValidation';
import { run_schedule_batch_validation, schedule_batch_default_state } from 'modals/batches/constants';
import useForceUpdate from 'modals/parameters/components/hooks/useForceUpdate';
import { Parameters } from 'modals/parameters/components/Parameters';
import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getValidationError, handleError } from 'utils/errors';
import { createParameters, getFormWarnings } from 'utils/helpers';
import { mergeSearchQuery } from 'utils/queries';
import { v4 as uuidv4 } from 'uuid';

import { useStepper } from './hooks/usestepper';

import cn from 'modals/batches/components/Components.module.scss';

export const RunScheduleBatch = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const run_schedule_batch = useAppSelector(selectRunScheduleBatch, shallowEqual);
  const scheduled_batch = useAppSelector(selectScheduledBatch);
  const parameters = useAppSelector(selectScheduledBatchParameters);

  const [batch_parameters, setBatchParameters] = useState<ProjectParameters>([]);
  const { force_update, handleUpdateDataFromModal } = useForceUpdate(setBatchParameters);
  const { page, wizard_width, reset, onNextStep, onPrevStep } = useStepper(0);

  const [form, setForm] = useState<ScheduleBatchPayload>({ ...schedule_batch_default_state });
  const { errors, validate, resetFieldError } = useValidate(form, run_schedule_batch_validation);

  useEffect(() => {
    setForm({ ...schedule_batch_default_state, ...scheduled_batch });
  }, [scheduled_batch]);

  useEffect(() => {
    setBatchParameters(parameters);
  }, [parameters]);

  const onChange = useCallback(
    (item: Partial<ScheduleBatchPayload>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const onNext = useCallback(() => {
    const is_valid = validate();

    if (!is_valid && !!errors.length) {
      dispatch(setModal({ warning: true }));
      dispatch(updateSettings({ warning: getFormWarnings(errors) }));
    }

    if (is_valid) {
      onNextStep();
    }
  }, [dispatch, errors, onNextStep, validate]);

  const { batch_name } = form;

  const closeModal = useCallback(() => {
    reset();
    setBatchParameters([]);
    dispatch(setModal({ run_schedule_batch: false }));
  }, [dispatch, reset]);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const is_valid = validate();

      if (!is_valid) return;

      try {
        const { scheduled_batch_id } = scheduled_batch;
        const queries = mergeSearchQuery(search, { update: uuidv4() });

        if (!scheduled_batch_id) return;

        const payload = {
          batch_name,
          parameters: createParameters(batch_parameters),
        };

        const res: any = await runScheduledBatch(scheduled_batch_id, payload);
        if (res?.error) return;

        closeModal();
        navigate(`${pathname}${queries}`);
        dispatch(updateSuccess(`Scheduled Batch is running successfully`));
      } catch (err) {
        handleError(err);
      }
    },
    [batch_name, batch_parameters, closeModal, dispatch, navigate, pathname, scheduled_batch, search, validate],
  );

  const setParameters = useCallback(
    (row: ProjectParameters[number], force_update?: boolean) => {
      handleUpdateDataFromModal(row, force_update);
    },
    [handleUpdateDataFromModal],
  );

  const handleReturn = useCallback(() => {
    if (page === 1) {
      onPrevStep();
    } else {
      closeModal();
    }
  }, [closeModal, onPrevStep, page]);

  return (
    <Modal
      name="run_schedule_batch"
      show={run_schedule_batch}
      width={wizard_width}
      padding={0}
      close_box={50}
      onClose={closeModal}
      standard={false}
    >
      <ModalHeader onReturn={handleReturn} page={page}>
        Run Scheduled Batch
      </ModalHeader>
      <form className={cn.overrideWizard} onSubmit={onSubmit}>
        {page === 0 && (
          <Input
            auto_focus
            label="Batch Name"
            value={batch_name}
            onChange={(value: string) => onChange({ batch_name: value })}
            error_message={errors.find((error) => error.key === 'batch_name')?.error}
            error_level={getValidationError({ errors, key: 'batch_name' }) ? ERROR_LEVEL.ERROR : undefined}
          />
        )}
        {page === 1 && (
          <Parameters force_update={force_update} setParameters={setParameters} parameters={batch_parameters} />
        )}
        <BottomNavigation current={page} end={1} onBack={onPrevStep} onNext={onNext} onClose={closeModal} />
      </form>
    </Modal>
  );
};
